// src/pages/CssPage.js
import React, { useState } from 'react';
import PillButton from '../components/PillButton';
import CodePlayground from '../components/CodePlayground';
import Quiz from '../components/Quiz';
import CopyButton from '../components/CopyButton';

function CssPage() {
  const [fontDemoStyle, setFontDemoStyle] = useState({});
  const [boxDemoStyle, setBoxDemoStyle] = useState({});

  const toggleFont = () => {
    setFontDemoStyle((prevStyle) =>
      prevStyle.fontFamily
        ? {}
        : { fontFamily: 'Arial, sans-serif', fontSize: '24px', fontWeight: 'bold' }
    );
  };


  const toggleBox = () => {
    const borderColor = document.documentElement.getAttribute('data-theme') === 'light' ? 'black' : 'white';
    setBoxDemoStyle((prevStyle) =>
      prevStyle.margin
        ? { border: '1px solid var(--secondary-color)' }
        : { margin: '20px', padding: '15px', border: `2px solid ${borderColor}` }
    );
  };

  return (
    <div>
      {/* Hero Section */}
      <header className="hero-section text-center" role="banner">
        <div className="container">
          <h1>CSS</h1>
          <p className="lead">Style your web pages</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-5" role="main">
        <section id="css-content" aria-labelledby="css-heading">
          <h2 id="css-heading">Learn CSS Basics</h2>
          <p>CSS (Cascading Style Sheets) controls how your HTML looks. Explore key concepts below!</p>

          {/* Chunk 1: Adding CSS */}
          <div className="chunk">
            <h3>1. Adding CSS</h3>
            <p>Use <code>&lt;style&gt;</code> in the head, external files, or inline styles.</p>
            <div className="code-snippet">
              <pre>
                {`{/* Inline */}
<p style="color: blue;">Blue text</p>

{/* Internal */}
<style>
  p { color: red; }
</style>

{/* External */}
<link rel="stylesheet" href="styles.css">`}

              <CopyButton text={`/* Inline */
<p style="color: blue;">Blue text</p>

/* Internal */
<style>
  p { color: red; }
</style>

/* External */
<link rel="stylesheet" href="styles.css">`} />
              </pre>
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Type <code>link</code> and press <strong>Tab</strong> to auto-generate a stylesheet link.
            </div>
          </div>

          {/* Chunk 2: Colors & Playground */}
          <div className="chunk">
            <h3>2. Colors & Playground</h3>
            <p>Change text and background colors with properties like <code>color</code> and <code>background-color</code>.</p>
            <CodePlayground initialCode={`/* Try changing these colors! */
p {
  color: #ff0000;
  background-color: #000000;
  padding: 10px;
}

<p>I am red text on black!</p>`} />
          </div>

          {/* Chunk 3: Fonts */}
          <div className="chunk">
            <h3>3. Fonts</h3>
            <p>Control text appearance with <code>font-family</code>, <code>font-size</code>, and <code>font-weight</code>.</p>
            <div className="code-snippet">
              <pre>
                {`h1 {
  font-family: Arial, sans-serif;
  font-size: 24px;
  font-weight: bold;
}`}

              </pre>
              <CopyButton text={`h1 {
  font-family: Arial, sans-serif;
  font-size: 24px;
  font-weight: bold;
}`} />
            </div>
            <p><strong>Try it:</strong> Click to toggle font!</p>
            <PillButton onClick={toggleFont}>Toggle Font</PillButton>
            <div id="font-demo" style={fontDemoStyle}>Font Demo</div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Use <strong>Cmd + .</strong> (Mac) or <strong>Ctrl + .</strong> (Windows) to quick-fix suggestions (e.g., add missing semicolons).
            </div>
          </div>

          {/* Chunk 4: Box Model */}
          <div className="chunk">
            <h3>4. Box Model</h3>
            <p>Every element is a box with <code>margin</code>, <code>padding</code>, <code>border</code>, and content.</p>
            <div className="code-snippet">
              <pre>
                {`div {
  margin: 20px; /* Outside space */
  padding: 15px; /* Inside space */
  border: 2px solid black;
}`}
              </pre>
            </div>
            <p><strong>Try it:</strong> Click to toggle box model!</p>
            <PillButton onClick={toggleBox}>Toggle Box</PillButton>
            <div id="box-demo" style={boxDemoStyle}>Box Demo</div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Hover over CSS properties and press <strong>Cmd</strong> (Mac) or <strong>Ctrl</strong> (Windows) to peek at definitions.
            </div>
          </div>

          {/* Chunk 5: Selectors */}
          <div className="chunk">
            <h3>5. Selectors</h3>
            <p>Target elements with tag, class (<code>.</code>), or ID (<code>#</code>) selectors.</p>
            <div className="code-snippet">
              <pre>
                {`p { color: blue; } /* Tag */
.my-class { font-size: 18px; } /* Class */
#my-id { background: yellow; } /* ID */`}
              </pre>
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Use <strong>Cmd + Shift + F</strong> (Mac) or <strong>Ctrl + Shift + F</strong> (Windows) to search across all files for selectors.
            </div>
          </div>

          {/* Chunk 6: Positioning */}
          <div className="chunk">
            <h3>6. Positioning</h3>
            <p>Control layout with <code>position</code> (static, relative, absolute, fixed).</p>
            <div className="code-snippet">
              <pre>
                {`.box {
  position: absolute;
  top: 10px;
  left: 10px;
}`}
              </pre>
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Press <strong>Cmd + B</strong> (Mac) or <strong>Ctrl + B</strong> (Windows) to toggle the sidebar for more workspace.
            </div>
          </div>

          {/* Best Practices and Variables */}
          <div className="chunk">
            <h3>Best Practices and CSS Variables</h3>
            <p>Writing clean, maintainable CSS is key. Here’s how to do it:</p>

            <h4>Best Practices</h4>
            <ul>
              <li><strong>Use Meaningful Names:</strong> Classes like <code>.button</code> are better than <code>.btn1</code>.</li>
              <li><strong>Keep it DRY:</strong> Don’t Repeat Yourself—reuse styles with classes or variables.</li>
              <li><strong>Organize Files:</strong> Separate CSS into logical files (e.g., <code>layout.css</code>, <code>theme.css</code>).</li>
              <li><strong>Use Comments:</strong> Explain complex styles with <code>{`/* comments */`}</code>.</li>
              <li><strong>Minimize Specificity:</strong> Avoid overusing IDs or deep nesting (e.g., <code>#main .section p</code>).</li>
            </ul>

            <h4>CSS Variables (Custom Properties)</h4>
            <p>Variables make styles easy to update globally. Define them in <code>:root</code> for global scope.</p>
            <div className="code-snippet">
              <pre>
                {`:root {
  --main-color: #000000;
  --spacing: 20px;
}
p {
  color: var(--main-color);
  margin: var(--spacing);
}`}
              </pre>
            </div>
            <p><strong>Why Use Variables?</strong></p>
            <ul>
              <li><strong>Consistency:</strong> Change one value, update everywhere.</li>
              <li><strong>Theming:</strong> Easily switch between light/dark modes (like this site!).</li>
              <li><strong>Readability:</strong> Names like <code>--main-color</code> are self-explanatory.</li>
            </ul>
            <p><strong>Try it:</strong> This site uses variables! Check the styles to see them in action.</p>
            <div className="tip">
              <strong>VS Code Tip:</strong> Press <strong>Cmd + Shift + R</strong> (Mac) or <strong>Ctrl + Shift + R</strong> (Windows) to refactor variable names across your project.
            </div>
          </div>
          
          {/* Quiz Section */}
          <div className="mt-5">
            <h3>Test Your Knowledge</h3>
            <Quiz 
              question="Which property controls the space INSIDE an element's border?"
              options={["margin", "padding", "border", "spacing"]}
              correctAnswerIndex={1}
            />
          </div>
        </section>
      </main>
    </div>
  );
}

export default CssPage;